import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'

/**
 * ダイアログ状態の列挙型
 */
export enum DialogState {
  IDLE = 'IDLE',
  LISTENING = 'LISTENING',
  USER_SPEAKING = 'USER_SPEAKING',
  PROCESSING = 'PROCESSING',
  AI_SPEAKING = 'AI_SPEAKING',
}

/**
 * ダイアログステートマシンサービス
 *
 * @description
 * ロールプレイセッション中のダイアログ状態遷移を管理する有限ステートマシン。
 * 5つの状態（IDLE、LISTENING、USER_SPEAKING、PROCESSING、AI_SPEAKING）と
 * それらの間の許可された遷移を定義し、無効な遷移を防止する。
 *
 * @responsibilities
 * - ダイアログ状態の遷移ロジック管理
 * - 状態遷移バリデーション（許可された遷移のみ実行）
 * - 状態変化のObservable発行
 *
 * @dependencies
 * - RxJS BehaviorSubject（状態ストリーム管理）
 *
 * @example
 * ```typescript
 * constructor(private dialogueSM: DialogueStateMachineService) {}
 *
 * ngOnInit() {
 *   // 状態変化を購読
 *   this.dialogueSM.getCurrentState().subscribe(state => {
 *     console.log('現在の状態:', state);
 *   });
 *
 *   // 状態遷移
 *   if (this.dialogueSM.transition(DialogState.LISTENING)) {
 *     console.log('LISTENING状態に遷移しました');
 *   }
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class DialogueStateMachineService {
  /**
   * 現在の状態を保持するBehaviorSubject
   */
  private currentState$ = new BehaviorSubject<DialogState>(DialogState.IDLE)

  /**
   * 状態遷移テーブル
   * 各状態から遷移可能な状態のリスト
   */
  private readonly transitionTable: Record<DialogState, DialogState[]> = {
    [DialogState.IDLE]: [DialogState.LISTENING],
    [DialogState.LISTENING]: [DialogState.USER_SPEAKING, DialogState.IDLE],
    [DialogState.USER_SPEAKING]: [DialogState.PROCESSING, DialogState.IDLE],
    [DialogState.PROCESSING]: [DialogState.AI_SPEAKING, DialogState.IDLE],
    [DialogState.AI_SPEAKING]: [DialogState.IDLE, DialogState.LISTENING],
  }

  /**
   * 現在のダイアログ状態を取得
   *
   * @returns 状態のObservableストリーム
   */
  getCurrentState(): Observable<DialogState> {
    return this.currentState$.asObservable()
  }

  /**
   * 新しい状態へ遷移
   *
   * @param newState 遷移先の状態
   * @returns 遷移成功ならtrue、無効遷移ならfalse
   * @throws Error 遷移失敗時にログ出力
   */
  transition(newState: DialogState): boolean {
    const currentState = this.currentState$.value

    // 既に同じ状態の場合は何もしない（冪等）
    if (currentState === newState) {
      return true
    }

    if (!this.canTransition(currentState, newState)) {
      console.warn(
        `[DialogueStateMachine] 無効な状態遷移を検出: ${currentState} → ${newState}`,
      )
      return false
    }

    this.currentState$.next(newState)
    console.log(
      `[DialogueStateMachine] 状態遷移: ${currentState} → ${newState}`,
    )
    return true
  }

  /**
   * 遷移が許可されているかチェック
   *
   * @param from 現在の状態
   * @param to 遷移先の状態
   * @returns 許可されていればtrue
   */
  canTransition(from: DialogState, to: DialogState): boolean {
    // 同じ状態への遷移は常に許可（冪等）
    if (from === to) {
      return true
    }

    const allowedTransitions = this.transitionTable[from]
    return allowedTransitions.includes(to)
  }
}
